/**
 * CommentsViewerPanel — Comments en modo viewer (Doc 21 Fase 5.e+).
 *
 * Versión "panel content" del antiguo `CommentsDrawer`, diseñada para
 * vivir DENTRO de `<RightSidePanel>` (mismo container que el editor),
 * en lugar de un drawer modal-overlay.
 *
 * Diferencias vs `CommentsPanel` (editor):
 *   - Viewer lista TODOS los comments de la versión (no filtra v−1).
 *   - Viewer permite crear NUEVOS root comments con excerpt capture
 *     (rol del reviewer auditando el doc).
 *   - Viewer permite editar/borrar own (CommentThreadCard con
 *     showEditDeleteActions=true).
 *   - Editor solo muestra v−1 sin form de "agregar nuevo" — el autor
 *     responde y transiciona, no crea nuevos.
 *
 * Comparten:
 *   - `<RightSidePanel>` como container persistente (área-bound entre
 *     project tabs y viewport bottom).
 *   - `<CommentThreadCard>` para el render del thread.
 *   - Status transitions y replies con permisos por rol.
 *
 * El header con título y close button lo provee `<RightSidePanel>`.
 * Aquí solo se renderiza body + footer form.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { AlertCircle, Loader2, Quote, Send, X } from 'lucide-react'

import { ApiError } from '../../api/client'
import {
  createComment,
  createReply,
  deleteComment,
  listAllComments,
  listVersionComments,
  updateComment,
  updateCommentStatus,
  type CommentStatus,
} from '../../api/comments'
import { listProjectMembers } from '../../api/project_members'
import { useAccessToken } from '../../auth/useAccessToken'
import { useMe } from '../../auth/useMe'
import { useTextSelection } from '../../hooks/useTextSelection'
import { canCommentOnArtifact, canReopenComment, isExternal } from '../../lib/tiers'
import { pushToast } from '../../stores/toastStore'
import { parseInstanceKey } from './comments/commentAnchorText'
import {
  CommentThreadCard,
  groupCommentsByThread,
} from './comments/CommentThreadCard'
import { CommentsHistoryView } from './comments/CommentsHistoryView'
import {
  CommentsViewToggle,
  type CommentsViewKey,
} from './comments/CommentsViewToggle'
import {
  getValueAtPath,
  type TemplateI18nSection,
  type TemplateSectionMeta,
} from './official-viewer/section_types'

// ─── Types ──────────────────────────────────────────────────────────────────
export interface CommentsViewerPanelProps {
  artifactId: string
  versionId: string
  projectId: string
  /** ID del usuario actual — para badges, permission gates. */
  currentUserId: string | null
  /** ID del autor del artifact — para gates de status transitions
   *  (pending → addressed solo lo puede hacer el autor). */
  artifactAuthorId: string | null
  /** OT-0009 — espejo de `_is_transition_allowed`: reponer un
   *  comentario resuelto exige ser el líder del proyecto, sin
   *  excepción de admin. El caller ya lo calcula (mismo `isLead` de
   *  `useProjectAccess` que usa para sus propios gates de edición). */
  isLead: boolean
  /** Secciones del template del artifact, para listar opciones en el
   *  selector de "agregar comentario a sección X" y para resolver
   *  títulos legibles de los grupos. */
  templateSections: TemplateSectionMeta[]
  templateI18nMap: Map<string, TemplateI18nSection>
  /** Doc 21 Fase 10 — content vivo de la versión para resolver el
   *  valor anclado a cada comment y mostrar el diff snapshot vs vivo
   *  en `CommentDiffBlock`. Cuando no se pasa, el bloque cae al render
   *  original sin comparación. */
  versionContent?: Record<string, unknown> | null
  /** Callback cuando cambia el count de comments. Lo usa el padre
   *  para mostrar el badge en el tab del RightSidePanel. */
  onCountChange?: (count: number) => void
}

// ─── Component ──────────────────────────────────────────────────────────────
export function CommentsViewerPanel({
  artifactId,
  versionId,
  projectId,
  currentUserId,
  artifactAuthorId,
  isLead,
  templateSections,
  templateI18nMap,
  versionContent,
  onCountChange,
}: CommentsViewerPanelProps) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const { me, isLoading: meIsLoading } = useMe()
  const queryClient = useQueryClient()

  // ─── Sección del nuevo comment ────────────────────────────────────────
  // `pickedSectionId` es lo que el user eligió (en el combo o al
  // seleccionar texto); mientras no elija, la primera sección.
  const flatSections = useMemo(
    () => flattenSections(templateSections),
    [templateSections],
  )
  const [pickedSectionId, setPickedSectionId] = useState<string | null>(null)
  const newSectionId = pickedSectionId ?? flatSections[0]?.section_id ?? ''

  // Doc 21 Fase 5.e+: cuando el user selecciona texto en una sección
  // del artifact, auto-poblamos el combo de "SECCIÓN" para que no
  // tenga que elegirla a mano. Solo si la sección existe en el template
  // (en el listado del select).
  const selectCapturedSection = useCallback(
    (sectionId: string) => {
      if (flatSections.some((s) => s.section_id === sectionId)) {
        setPickedSectionId(sectionId)
      }
    },
    [flatSections],
  )
  const { snapshot: capturedExcerpt, clear: clearExcerpt } = useTextSelection(
    { onCapture: selectCapturedSection },
  )

  // Toggle Activos / Historial — paridad con el editor (`CommentsPanel`).
  // Activos = comments de ESTA versión (lo que el lector está viendo).
  // Historial = todos los comments del artifact a través de versiones,
  // agrupados por (version DESC, cycle DESC) — útil para auditar la
  // evolución del documento. Misma UX que el editor.
  const [view, setView] = useState<CommentsViewKey>('active')

  // ─── Queries ──────────────────────────────────────────────────────────
  const commentsQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'versions', versionId, 'comments'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listVersionComments(accessToken, artifactId, versionId)
    },
    enabled: !!accessToken,
    staleTime: 15_000,
  })

  // Historial completo — lazy: solo carga cuando el user activa la
  // pestaña Historial.
  const historyQuery = useQuery({
    queryKey: ['artifacts', artifactId, 'all-comments'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listAllComments(accessToken, artifactId)
    },
    enabled: !!accessToken && view === 'history',
    staleTime: 15_000,
  })

  // OT-0009 M4 — deshabilitada para `external`: `canCommentOnArtifact` ya
  // lo deja pasar sin mirar `isMember` (ver `lib/tiers.ts`), así que esta
  // query no le aporta nada a ese tier — solo generaba un 404 de más
  // (`can_view_project`/`require_project_view` siempre rechaza a external
  // al listar members). `!meIsLoading` evita la carrera con `useMe()`.
  const membersQuery = useQuery({
    queryKey: ['projects', projectId, 'members'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listProjectMembers(accessToken, projectId)
    },
    enabled: !!accessToken && !meIsLoading && !isExternal(me?.role),
    staleTime: 60_000,
  })

  const displayNameByUserId = useMemo(() => {
    const m = new Map<string, string>()
    for (const member of membersQuery.data ?? []) {
      if (member.user_id) {
        m.set(
          member.user_id,
          member.user_display_name ??
            member.external_email ??
            t('comments_drawer.unknown_user'),
        )
      }
    }
    return m
  }, [membersQuery.data, t])

  // OT-0009 M4 — espejo de `can_comment_on_artifact`. Sin esto, un
  // interno que solo ve el proyecto por `can_view_all_projects` (no es
  // `project_members`) veía el form de comentario nuevo y los botones
  // de responder/transicionar/editar/borrar, y el backend los rechazaba
  // con 404 al enviarlos.
  const isMember = useMemo(
    () => (membersQuery.data ?? []).some((m) => m.user_id === currentUserId),
    [membersQuery.data, currentUserId],
  )
  const canComment = canCommentOnArtifact(me?.role, me?.tenant?.access, { isMember })
  // OT-0009 — hallazgo del sweep adversarial: "Reponer" tenía una
  // regla más estricta que las demás transiciones (solo el líder,
  // sin excepción de admin) y quedaba gateado con `canComment` a
  // secas, igual que las otras 5 acciones de comentario.
  const canReopen = canReopenComment(me?.tenant?.access, { isLead })

  const threads = useMemo(
    () => groupCommentsByThread(commentsQuery.data ?? []),
    [commentsQuery.data],
  )

  // Doc 21 Fase 10 — resuelve el valor vivo de la sección anclada a un
  // comment. Si no hay versionContent (caller no lo pasó) o el path no
  // resuelve, regresa undefined y el CommentDiffBlock omite la
  // comparación.
  const getLiveSectionValue = useMemo(() => {
    return (instanceKey: string): unknown => {
      if (!versionContent) return undefined
      const path = parseInstanceKey(instanceKey)
      if (path.length === 0) return undefined
      return getValueAtPath(versionContent, path)
    }
  }, [versionContent])

  // Solo cuenta threads de comentario (kind='comment'); las replies y
  // los eventos `status_change` también vienen en la respuesta pero no
  // son threads visibles — contarlos infla el badge artificialmente.
  const totalCount = useMemo(
    () =>
      (commentsQuery.data ?? []).filter((r) => r.kind === 'comment').length,
    [commentsQuery.data],
  )
  useEffect(() => {
    onCountChange?.(totalCount)
  }, [totalCount, onCountChange])

  // ─── Mutations ────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (input: {
      section_id: string
      body: string
      excerpt?: string | null
    }) => {
      if (!accessToken) throw new Error('no_access_token')
      const body: {
        section_id: string
        body: string
        excerpt?: string | null
      } = {
        section_id: input.section_id,
        body: input.body,
      }
      if (input.excerpt) body.excerpt = input.excerpt
      return createComment(accessToken, artifactId, versionId, body)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          'artifacts',
          artifactId,
          'versions',
          versionId,
          'comments',
        ],
      })
      pushToast({
        kind: 'success',
        message: t('comments_drawer.toast.created'),
      })
    },
    onError: (err) => showApiToast(err, 'comments_drawer.toast.create_failed', t),
  })

  const updateMutation = useMutation({
    mutationFn: (input: { commentId: string; body: string }) => {
      if (!accessToken) throw new Error('no_access_token')
      return updateComment(accessToken, input.commentId, {
        body: input.body,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          'artifacts',
          artifactId,
          'versions',
          versionId,
          'comments',
        ],
      })
      pushToast({
        kind: 'success',
        message: t('comments_drawer.toast.updated'),
      })
    },
    onError: (err) => showApiToast(err, 'comments_drawer.toast.update_failed', t),
  })

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => {
      if (!accessToken) throw new Error('no_access_token')
      return deleteComment(accessToken, commentId)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          'artifacts',
          artifactId,
          'versions',
          versionId,
          'comments',
        ],
      })
      pushToast({
        kind: 'success',
        message: t('comments_drawer.toast.deleted'),
      })
    },
    onError: (err) => showApiToast(err, 'comments_drawer.toast.delete_failed', t),
  })

  const replyMutation = useMutation({
    mutationFn: (input: { commentId: string; body: string }) => {
      if (!accessToken) throw new Error('no_access_token')
      return createReply(accessToken, input.commentId, { body: input.body })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          'artifacts',
          artifactId,
          'versions',
          versionId,
          'comments',
        ],
      })
    },
    onError: (err) =>
      showApiToast(err, 'comments_panel.toast.reply_failed', t),
  })

  const statusMutation = useMutation({
    mutationFn: (input: {
      commentId: string
      status: CommentStatus
      note?: string | null
    }) => {
      if (!accessToken) throw new Error('no_access_token')
      const body: { status: CommentStatus; note?: string | null } = {
        status: input.status,
      }
      if (input.note !== undefined) body.note = input.note
      return updateCommentStatus(accessToken, input.commentId, body)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          'artifacts',
          artifactId,
          'versions',
          versionId,
          'comments',
        ],
      })
    },
    onError: (err) =>
      showApiToast(err, 'comments_panel.toast.status_failed', t),
  })

  // ─── Local UI state (form de nuevo comment) ───────────────────────────
  const [newBody, setNewBody] = useState<string>('')

  function handleSubmitNew(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = newBody.trim()
    if (!trimmed || !newSectionId) return
    createMutation.mutate(
      {
        section_id: newSectionId,
        body: trimmed,
        excerpt: capturedExcerpt,
      },
      {
        onSuccess: () => {
          setNewBody('')
          clearExcerpt()
        },
      },
    )
  }

  // ─── Render gates ─────────────────────────────────────────────────────
  const isLoading =
    meIsLoading || commentsQuery.isLoading || membersQuery.isLoading
  const hasError = commentsQuery.error

  // Loading/error del Historial cuando esa vista está activa.
  const isLoadingHistory = view === 'history' && historyQuery.isLoading
  const hasHistoryError = view === 'history' && historyQuery.error

  return (
    <div className="flex flex-col h-full">
      <CommentsViewToggle view={view} onChange={setView} />
      {/* Body — lista de threads (Activos) o historial agrupado */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {view === 'active' ? (
          isLoading ? (
            <div className="flex items-center justify-center py-8 text-ink-subtle">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              <span className="text-sm">{t('comments_drawer.loading')}</span>
            </div>
          ) : hasError ? (
            <div className="m-5 rounded-md border border-critical/40 bg-critical-soft p-3 text-xs text-critical flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>{t('comments_drawer.error_loading')}</span>
            </div>
          ) : threads.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-ink-muted italic">
              {t('comments_drawer.empty')}
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {threads.map((thread) => (
                <li key={thread.root.id}>
                  <CommentThreadCard
                    thread={thread}
                    currentUserId={currentUserId}
                    artifactAuthorId={artifactAuthorId}
                    displayNameByUserId={displayNameByUserId}
                    sectionTitle={sectionTitle(
                      thread.root.section_id,
                      thread.root.instance_key,
                      templateI18nMap,
                    )}
                    liveSectionValue={getLiveSectionValue(
                      thread.root.instance_key,
                    )}
                    isReplying={replyMutation.isPending}
                    isStatusChanging={statusMutation.isPending}
                    isDeleting={deleteMutation.isPending}
                    showEditDeleteActions={true}
                    showStatusActions={true}
                    onReply={
                      canComment
                        ? (body) =>
                            replyMutation.mutate({
                              commentId: thread.root.id,
                              body,
                            })
                        : undefined
                    }
                    onMarkAddressed={
                      canComment
                        ? (note) =>
                            statusMutation.mutate({
                              commentId: thread.root.id,
                              status: 'addressed',
                              note: note ?? null,
                            })
                        : undefined
                    }
                    onResolve={
                      canComment
                        ? () =>
                            statusMutation.mutate({
                              commentId: thread.root.id,
                              status: 'resolved',
                            })
                        : undefined
                    }
                    onReopen={
                      canReopen
                        ? () =>
                            statusMutation.mutate({
                              commentId: thread.root.id,
                              status: 'pending',
                            })
                        : undefined
                    }
                    onEditOwn={
                      canComment
                        ? (commentId, newBodyValue) =>
                            updateMutation.mutate({
                              commentId,
                              body: newBodyValue,
                            })
                        : undefined
                    }
                    onDeleteOwn={
                      canComment
                        ? (commentId) => deleteMutation.mutate(commentId)
                        : undefined
                    }
                  />
                </li>
              ))}
            </ul>
          )
        ) : isLoadingHistory ? (
          <div className="flex items-center justify-center py-8 text-ink-subtle">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            <span className="text-sm">{t('comments_drawer.loading')}</span>
          </div>
        ) : hasHistoryError ? (
          <div className="m-5 rounded-md border border-critical/40 bg-critical-soft p-3 text-xs text-critical flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{t('comments_drawer.error_loading')}</span>
          </div>
        ) : (
          <CommentsHistoryView
            comments={historyQuery.data ?? []}
            currentUserId={currentUserId}
            artifactAuthorId={artifactAuthorId}
            displayNameByUserId={displayNameByUserId}
            templateI18nMap={templateI18nMap}
          />
        )}
      </div>

      {/* Footer — form de nuevo root comment con excerpt capture
          automático: el hook `useTextSelection` escucha mouseup
          globalmente y captura cuando el user termina de seleccionar
          texto dentro de cualquier sección del artifact (filtra por
          `data-section-id`). El user no necesita hacer nada extra.
          Solo visible en la vista Activos — en Historial no aplica
          crear comments nuevos (es read-only de auditoría). OT-0009 M4:
          tampoco se muestra si el tier/membresía actual no puede
          comentar (`canComment`) — mismo criterio que "Crear artefacto
          en esta fase" en `PhaseDrillIn`: se oculta, no se deshabilita,
          porque no es una acción sobre ESTE objeto sino elegibilidad
          para participar en el proyecto. */}
      {view === 'active' && canComment ? (
      <form
        onSubmit={handleSubmitNew}
        className="border-t border-line bg-surface px-5 py-3 space-y-2 shrink-0"
      >
        {capturedExcerpt ? (
          <div className="flex items-start gap-2 rounded-md border border-brand/30 bg-brand-soft/40 px-2 py-1.5">
            <Quote className="w-3 h-3 text-brand shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-brand">
                {t('comments_drawer.excerpt_label')}
              </p>
              <p className="text-[11px] italic text-ink-muted line-clamp-2">
                {capturedExcerpt}
              </p>
            </div>
            <button
              type="button"
              onClick={clearExcerpt}
              className="text-ink-subtle hover:text-ink shrink-0"
              aria-label={t('comments_drawer.excerpt_clear')}
              title={t('comments_drawer.excerpt_clear')}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <p className="text-[10px] text-ink-subtle italic px-2 py-1 border-l-2 border-line">
            {t('comments_drawer.excerpt_capture_hint')}
          </p>
        )}

        <div className="flex items-center gap-2">
          <label
            htmlFor="comments-viewer-section"
            className="text-[10px] uppercase tracking-wider text-ink-subtle font-semibold shrink-0"
          >
            {t('comments_drawer.section_label')}
          </label>
          <select
            id="comments-viewer-section"
            value={newSectionId}
            onChange={(e) => setPickedSectionId(e.target.value)}
            disabled={createMutation.isPending || flatSections.length === 0}
            className="flex-1 bg-canvas border border-line rounded px-2 py-1 text-xs focus:outline-none focus:border-brand disabled:opacity-50"
          >
            {flatSections.map((s) => (
              <option key={s.section_id} value={s.section_id}>
                {templateI18nMap.get(s.section_id)?.title ?? s.section_id}
              </option>
            ))}
          </select>
        </div>
        <textarea
          value={newBody}
          onChange={(e) => setNewBody(e.target.value)}
          rows={2}
          maxLength={5000}
          placeholder={t('comments_drawer.placeholder')}
          disabled={createMutation.isPending}
          className="w-full bg-canvas border border-line rounded px-2 py-1.5 text-xs focus:outline-none focus:border-brand disabled:opacity-50"
        />
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] text-ink-subtle">
            {newBody.length} / 5000
          </span>
          <button
            type="submit"
            disabled={
              createMutation.isPending || !newBody.trim() || !newSectionId
            }
            className="inline-flex items-center gap-1 px-3 py-1 bg-brand hover:bg-brand-deep text-canvas text-xs font-medium rounded disabled:opacity-50"
          >
            {createMutation.isPending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Send className="w-3 h-3" />
            )}
            {t('comments_drawer.submit')}
          </button>
        </div>
      </form>
      ) : null}
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function showApiToast(
  err: unknown,
  fallbackKey: string,
  t: TFunction,
): void {
  const code = err instanceof ApiError ? err.code : null
  const fallback = t(fallbackKey)
  const message = code
    ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
    : err instanceof Error
      ? err.message
      : fallback
  pushToast({ kind: 'critical', message, duration: 6000 })
}

function sectionTitle(
  sectionId: string,
  instanceKey: string,
  templateI18nMap: Map<string, TemplateI18nSection>,
): string {
  const i18n = templateI18nMap.get(sectionId)
  const base = i18n?.title ?? sectionId
  if (instanceKey && instanceKey !== sectionId) {
    return `${base} · ${instanceKey}`
  }
  return base
}

function flattenSections(
  nodes: TemplateSectionMeta[],
): TemplateSectionMeta[] {
  const out: TemplateSectionMeta[] = []
  for (const n of nodes) {
    const children = (n['children'] as TemplateSectionMeta[] | undefined) ?? []
    if (children.length === 0) {
      out.push(n)
    } else {
      out.push(...flattenSections(children))
    }
  }
  return out
}
